/* eslint-disable @tanstack/query/exhaustive-deps */
import { useInfiniteQuery, useQuery, useSuspenseInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";

import ContentRequests from "@/api/content/content.requests.ts";
import { useLang } from "@/hooks/useLang.ts";

interface GetContentProps {
  limit: number;
  category?: number;
  genres?: string | null;
  search?: string | null;
  content_lang?: string | null;
  country?: string | null;
  year?: string | null;
  ordering?: string | null;
  distributor?: string | null;
  is_free?: boolean | null;
}

interface ContentKey extends GetContentProps {
  lang: string;
}

export const CONTENT_KEYS = {
  contentDetail: (lang: string, id: number) => ["content-detail", lang, id].filter(Boolean),
  contentDetailPage: (lang: string, id: number, token: string) =>
    ["content-detail-page", lang, id, token].filter(Boolean),
  contentDetailRecommendation: (lang: string, id: number) =>
    ["content-detail-recommendation", lang, id].filter(Boolean),
  episodesOfContent: (lang: string, id: number) => ["content-detail-page-episodes", lang, id].filter(Boolean),
  banners: (lang: string) => ["banners", lang],
  catalog: (lang: string, id: number) => ["catalog", lang, id].filter(Boolean),
  catalogFilter: (lang: string, category: number, token: string) =>
    ["catalog-filter", lang, category, token].filter(Boolean),
  categories: (lang: string) => ["categories", lang],
  collections: (lang: string, isKids: boolean, token: string, search?: string) =>
    ["collections", lang, isKids, token, search].filter(Boolean),
  collectionDetail: (lang: string, id: number, limit: number, search?: string) =>
    ["collection-detail", lang, id, limit, search].filter(Boolean),
  content: (props: ContentKey) =>
    [
      "content",
      props.lang,
      props.limit,
      props.category,
      props.search,
      props.genres,
      props.content_lang,
      props.country,
      props.year,
      props.ordering,
      props.distributor,
      props.is_free,
    ].filter(Boolean),
  contentSearch: (lang: string, limit: number, search?: string | null) =>
    ["content-search", lang, limit, search].filter(Boolean),
  genres: (lang: string, limit: number) => ["genres", lang, limit].filter(Boolean),
  countries: (lang: string) => ["countries", lang].filter(Boolean),
  getPersonDetail: (lang: string, id: number) => ["person-detail", lang, id].filter(Boolean),
  getPersons: (lang: string, limit: number, search?: string) => ["persons", lang, limit, search].filter(Boolean),
  getPremiers: (lang: string, limit: number) => ["premiers", lang, limit].filter(Boolean),
  getRecommends: (lang: string) => ["recommends", lang].filter(Boolean),
  getDistributors: (lang: string) => ["distributors", lang].filter(Boolean),
  mainList: (lang: string) => ["main-list", lang],
  collectionList: (lang: string, token: string) => ["listOfCollection", lang, token],
  profileRecommendations: (lang: string) => ["profile-recommendations", lang],
  profileSelectedRecommendation: (lang: string) => ["profile-selected-recommendations", lang],
  getCategory: (lang: string) => ["category", lang],
  episodeInfo: (lang: string, id: number) => ["episode-info", lang, id].filter(Boolean),
};

export const useGetContentDetail = (id: number) => {
  const lang = useLang();

  return useQuery({
    queryKey: CONTENT_KEYS.contentDetail(lang, id),
    queryFn: () => ContentRequests.getContentDetail(id),
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetContentDetailPage = (id: number) => {
  const lang = useLang();
  const token = localStorage.getItem("splay:token") || "";

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.contentDetailPage(lang, id, token),
    queryFn: () => (token ? ContentRequests.getContentDetailPage(id) : ContentRequests.getContentDetailPageNoAuth(id)),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetRecommendationOfDetailPage = (id: number) => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.contentDetailRecommendation(lang, id),
    queryFn: () => ContentRequests.getContentDetailPageRecommendation(id),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetContentEpisodesSus = (id: number) => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.episodesOfContent(lang, id),
    queryFn: () => ContentRequests.getContentEpisodes(id),
    staleTime: 1000 * 60 * 15,
  });
};
export const useGetContentEpisodes = (id: number) => {
  const lang = useLang();

  return useQuery({
    queryKey: CONTENT_KEYS.episodesOfContent(lang, id),
    queryFn: () => ContentRequests.getContentEpisodes(id),
    staleTime: 1000 * 60 * 15,
    enabled: !!id,
  });
};
export const useGetEpisodeInfo = (id: number, enabled: boolean) => {
  const lang = useLang();

  return useQuery({
    queryKey: CONTENT_KEYS.episodeInfo(lang, id),
    queryFn: () => ContentRequests.getContentEpisodeInfo(id),
    staleTime: 1000 * 60 * 15,
    enabled: enabled,
  });
};

export const useGetBanners = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.banners(lang),
    queryFn: () => ContentRequests.getBanners(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetCatalogFilter = (category: number) => {
  const lang = useLang();
  const token = localStorage.getItem("splay:token") || "";

  return useQuery({
    queryKey: CONTENT_KEYS.catalogFilter(lang, category, token),
    queryFn: () => ContentRequests.getCatalogFilter(category),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetCategories = () => {
  const lang = useLang();

  return useQuery({
    queryKey: CONTENT_KEYS.categories(lang),
    queryFn: () => ContentRequests.getCategories(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetCollections = (search?: string) => {
  const lang = useLang();
  const token = localStorage.getItem("splay:token") || "";
  const profileAge = localStorage.getItem("splay:profileAge");
  const isKids: boolean = profileAge ? parseInt(profileAge) < 16 : false;

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.collections(lang, isKids, token, search),
    queryFn: () =>
      token ? ContentRequests.getCollections(isKids, search) : ContentRequests.getCollectionsNoAuth(search),
    staleTime: 1000 * 60 * 30,
  });
};

export const useGetContent = ({ limit, ...others }: GetContentProps) => {
  const lang = useLang();

  return useInfiniteQuery({
    queryKey: CONTENT_KEYS.content({ lang, limit, ...others }),
    queryFn: ({ pageParam = 0 }) => ContentRequests.getContent({ limit, offset: pageParam * limit, ...others }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetSearchContent = ({ limit, search }: GetContentProps) => {
  const lang = useLang();

  return useInfiniteQuery({
    queryKey: CONTENT_KEYS.contentSearch(lang, limit, search),
    queryFn: ({ pageParam = 0 }) => ContentRequests.getSearchContent({ limit, offset: pageParam * limit, search }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetGenres = (limit: number) => {
  const lang = useLang();

  return useSuspenseInfiniteQuery({
    queryKey: CONTENT_KEYS.genres(lang, limit),
    queryFn: ({ pageParam = 0 }) => ContentRequests.getGenres(limit, pageParam * limit),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length === limit ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetCountries = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.countries(lang),
    queryFn: () => ContentRequests.getCatalogFilterNoAuth().then((data) => data.countries),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetMainList = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.mainList(lang),
    queryFn: () => ContentRequests.getMainList(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetCollectionList = () => {
  const lang = useLang();
  const token = localStorage.getItem("splay:token") || "";

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.collectionList(lang, token),
    queryFn: () => (token ? ContentRequests.getCollectionList() : ContentRequests.getCollectionListNoAuth()),
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetPersons = (limit: number, search?: string) => {
  const lang = useLang();

  return useSuspenseInfiniteQuery({
    queryKey: CONTENT_KEYS.getPersons(lang, limit, search),
    queryFn: ({ pageParam = 0 }) => ContentRequests.getPersons(limit, pageParam * limit, search),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetPremiers = (limit: number) => {
  const lang = useLang();
  const token = localStorage.getItem("splay:token") || "";

  return useSuspenseInfiniteQuery({
    queryKey: CONTENT_KEYS.getPremiers(lang, limit),
    queryFn: ({ pageParam = 0 }) =>
      token
        ? ContentRequests.getPremiers(limit, pageParam * limit)
        : ContentRequests.getPremiersNoAuth(limit, pageParam * limit),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetRecommends = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.getRecommends(lang),
    queryFn: () => ContentRequests.getRecommends(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetDistributors = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.getDistributors(lang),
    queryFn: () => ContentRequests.getDistributors(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetCollectionDetail = ({ id, limit, search }: { id: number; limit: number; search?: string }) => {
  const lang = useLang();

  return useInfiniteQuery({
    queryKey: CONTENT_KEYS.collectionDetail(lang, id, limit, search),
    queryFn: ({ pageParam = 0 }) =>
      ContentRequests.getCollectionDetail({ id, limit, offset: pageParam * limit, search }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.contents.result.length === limit ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetProfileRecommendations = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.profileRecommendations(lang),
    queryFn: () => ContentRequests.getProfileRecommendations(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetSelectedRecommendations = (id: number) => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: CONTENT_KEYS.profileSelectedRecommendation(lang),
    queryFn: () => ContentRequests.getProfileSelectedRecommendations(id),
    staleTime: 0,
  });
};

export const useGetGenre = (id: number) => {
  const genres = useGetGenres(999);

  return genres.data.pages[0].find((item) => item.id == id);
};

export const useGetCountry = (id: number) => {
  const genres = useGetCountries();

  return genres.data.find((item) => item.id == id);
};

export const useGetCategory = (id: number) => {
  const categories = useGetCategories();

  return categories.data?.find((item) => item.id == id);
};
